import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import { fetchAuthSession } from "aws-amplify/auth";
import { Heart, Bookmark, Star, Cpu } from "lucide-react";
import { motion } from "framer-motion";
import mockData from "./mockData.json";

// Skeleton Loader Component
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded-md ${className}`} />
);

const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.cosmelytix.com';

const ProductProfilePage = () => {
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [productError, setProductError] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [added, setAdded] = useState(false);

  const fetchInteractionStatus = async (id, token) => {
    try {
      const [likeRes, saveRes, addRes] = await Promise.all([
        fetch(`${API_BASE}/interaction?productId=${id}&type=like`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/interaction?productId=${id}&type=save`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/interaction?productId=${id}&type=add`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const parseInteraction = async (res) => {
        const wrapper = await res.json();
        const data = typeof wrapper.body === 'string' ? JSON.parse(wrapper.body) : wrapper.body;
        return data?.status === 1;
      };

      setLiked(likeRes.status !== 404 ? await parseInteraction(likeRes) : false);
      setSaved(saveRes.status !== 404 ? await parseInteraction(saveRes) : false);
      setAdded(addRes.status !== 404 ? await parseInteraction(addRes) : false);
    } catch (error) {
      console.error("Error fetching interaction status:", error);
    }
  };

  const handleInteraction = async (type) => {
    const setter = type === "like" ? setLiked : type === "save" ? setSaved : setAdded;
    const current = type === "like" ? liked : type === "save" ? saved : added;
    setter(!current); // optimistic update
    try {
      const res = await fetch(`${API_BASE}/interaction`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ productId: String(productId), interactionType: type }),
      });
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
    } catch (error) {
      console.error(`Failed to record ${type}:`, error);
      setter(current); // revert on failure
    }
  };

  const fetchSimilarProducts = async (id, token) => {
    setLoadingSimilar(true);
    try {
      const response = await fetch(`${API_BASE}/products/${id}/similar`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      const items = (Array.isArray(data) ? data : (data.results ?? data.body ?? [])).slice(0, 3);

      // Step 1: show names + links immediately
      setSimilarProducts(items);
      setLoadingSimilar(false);

      // Step 2: fetch full details per card, update as each resolves
      items.forEach(async (item) => {
        try {
          const res = await fetch(`${API_BASE}/products/${item.prod_id}`, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          if (!res.ok) return;
          const detail = await res.json();
          setSimilarProducts((prev) =>
            prev.map((p) => (p.prod_id === item.prod_id ? { ...p, ...detail } : p))
          );
        } catch {
          // card stays without image
        }
      });
    } catch (error) {
      console.error("Error fetching similar products:", error);
      setLoadingSimilar(false);
    }
  };

  const fetchProductFromAPI = async (id, token) => {
    setLoadingProduct(true);
    setProductError(null);
    try {
      const response = await fetch(
        `${API_BASE}/products/${id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setProduct(data);
    } catch (error) {
      setProductError(error.message || "Error fetching product.");
      setProduct(null);
    } finally {
      setLoadingProduct(false);
    }
  };

  useEffect(() => {
    const checkAuthAndFetchProduct = async () => {
      setLoadingAuth(true);
      setAuthError(null);

      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        let idToken = null;
        if (typeof session.tokens?.idToken === "string") {
          idToken = session.tokens.idToken;
        } else if (session.tokens?.idToken?.jwtToken) {
          idToken = session.tokens.idToken.jwtToken;
        } else if (
          session.tokens?.idToken &&
          typeof session.tokens.idToken.toString === "function"
        ) {
          idToken = session.tokens.idToken.toString();
        }
        if (!idToken) {
          throw new Error("User is not authenticated (no ID token found)");
        }

        setIsAuthenticated(true);
        setIdToken(idToken);

        // Check mockData first
        const mockProduct = mockData.find((p) => p.id === productId);
        if (mockProduct) {
          setProduct(mockProduct);
        } else {
          await fetchProductFromAPI(productId, idToken);
        }

        // Fetch similar products + interaction status in parallel (non-blocking)
        fetchSimilarProducts(productId, idToken);
        fetchInteractionStatus(productId, idToken);
      } catch (error) {
        setAuthError(error.message || "User is not authenticated");
        setIsAuthenticated(false);
      } finally {
        setLoadingAuth(false);
      }
    };

    checkAuthAndFetchProduct();
  }, [productId]);


  if (loadingAuth || loadingProduct) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-[#E9A969] to-[#5F40A1]">
        <Header />
        <main className="flex-grow container mx-auto px-6 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 bg-[#CFC7DF] rounded-3xl shadow-xl overflow-hidden p-10">
            <Skeleton className="w-full h-96" />
            <div className="space-y-6">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-10 w-1/4" />
              <Skeleton className="h-40 w-full" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-indigo-50 to-white">
        <Header />
        <main className="flex-grow container mx-auto px-6 py-20 flex items-center justify-center">
          <div className="bg-[#CFC7DF] rounded-3xl shadow-xl p-10 max-w-md w-full text-center space-y-6">
            <div className="text-6xl">🔒</div>
            <h1 className="text-2xl font-bold text-gray-800">
              You must be logged in
            </h1>
            <p className="text-gray-600">
              Please sign in to view this product’s details and personalized recommendations.
            </p>
            <button
              onClick={() => (window.location.href = '/user-account-page')}
              className="mt-4 px-6 py-3 rounded-xl bg-indigo-600 text-white font-medium shadow hover:bg-indigo-700 transition"
            >
              Sign In
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }


  // Skeleton Loader State
  if (loadingProduct) {

    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <Header />
        <main className="flex-grow container mx-auto px-6 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 bg-[#CFC7DF] rounded-3xl shadow-xl overflow-hidden p-10">
            <Skeleton className="w-full h-96" />
            <div className="space-y-6">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-10 w-1/4" />
              <Skeleton className="h-40 w-full" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (productError) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-rose-50 to-pink-50">
        <Header />
        <main className="flex-grow container mx-auto px-6 py-14 flex items-center justify-center">
          <div className="bg-[#CFC7DF] rounded-3xl shadow-xl p-10 max-w-lg text-center space-y-6">
            <div className="text-6xl">😔</div>
            <h1 className="text-2xl font-bold text-gray-800">Something went wrong</h1>
            <p className="text-gray-600">
              We couldn’t load this product right now. Please try again in a moment.
            </p>
            {/* Show the real error message in a subtle way */}
            <p className="text-sm text-gray-400 italic">
              {productError}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-3 rounded-xl bg-pink-500 text-white font-medium shadow hover:bg-pink-600 transition"
            >
              Retry
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }


  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-[#E9A969] to-[#5F40A1]">
      <Header />

      <main className="flex-grow container mx-auto px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-14 bg-[#CFC7DF] rounded-3xl shadow-xl overflow-hidden"
        >
          {/* Image Section */}
          <div className="flex justify-center items-center bg-gray-100 p-8">
            <motion.img
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              src={"https://cosmelytix-products-imgs.s3.us-east-1.amazonaws.com/" + product.image_url}
              alt={product.name}
              className="rounded-2xl shadow-lg max-w-full max-h-96 w-auto h-auto object-contain hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Details Section */}
          <div className="flex flex-col justify-between p-10 space-y-8">
            <div>
              {product.brand && (
                <p className="text-sm font-medium text-[#5F40A1] uppercase tracking-widest mb-1">{product.brand}</p>
              )}
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-3">
                {product.name}
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-5">
                {product.description}
              </p>

              {product.dateAdded && (
                <p className="text-sm text-gray-400 italic">
                  Added on {new Date(product.dateAdded).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Likes / Saves */}
            <div className="flex gap-6 text-gray-600">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleInteraction("like")}
                className={`flex items-center gap-2 transition-colors ${liked ? "text-pink-600" : "hover:text-pink-600"}`}
              >
                <Heart className={`w-5 h-5 ${liked ? "fill-pink-600" : ""}`} />
                {liked ? "Liked" : "Like"}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleInteraction("save")}
                className={`flex items-center gap-2 transition-colors ${saved ? "text-[#5F40A1]" : "hover:text-[#5F40A1]"}`}
              >
                <Bookmark className={`w-5 h-5 ${saved ? "fill-[#5F40A1]" : ""}`} />
                {saved ? "Saved" : "Save"}
              </motion.button>
            </div>

            {/* Ingredients */}
            <div className="pt-4 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Ingredients
              </h2>
              {(() => {
                const all = (typeof product.ingredient_names === 'string'
                  ? product.ingredient_names.replace(/^\{|\}$/g, '').split(',')
                  : product.ingredient_names ?? []
                ).map(i => i.trim());
                const visible = showAllIngredients ? all : all.slice(0, 10);
                return (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {visible.map((ingredient, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-white text-gray-700 text-xs rounded-full border border-gray-200 shadow-sm"
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                    {all.length > 10 && (
                      <button
                        onClick={() => setShowAllIngredients(prev => !prev)}
                        className="mt-3 text-sm text-[#5F40A1] hover:underline"
                      >
                        {showAllIngredients ? 'Show less' : `Show all ${all.length} ingredients`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Ratings */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <div className="flex items-center gap-1 text-[#5F40A1]">
                <span className="font-medium">Recommended to others:</span>
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-6 h-6 ${i < Math.round(product.overallRating)
                      ? "text-[#C58348] fill-[#C58348]"
                      : "text-[#7A6E62]"
                      }`}
                  />
                ))}
                <span className="ml-2 text-sm text-gray-500">
                  {product.overallRating} / 5
                </span>
              </div>

              <p className="text-[#1E627D] font-semibold text-lg">
                Recommended Score: {product.recommendedScore}%
              </p>
            </div>

            {/* CTA: Add to Algorithm */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleInteraction("add")}
              className={`mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-lg font-semibold shadow-md transition ${added ? 'bg-[#E9A969] text-white hover:bg-[#d4945a]' : 'bg-[#5F40A1] text-white hover:bg-indigo-700'
                }`}
            >
              <Cpu className="w-5 h-5" /> {added ? 'Added to Algorithm' : 'Add to Algorithm'}
            </motion.button>
          </div>
        </motion.div>

        {/* Similar Products */}
        {(loadingSimilar || similarProducts.length > 0) && (
          <div className="mt-14">
            <h2 className="text-2xl font-bold text-white mb-6">Similar Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {loadingSimilar
                ? [...Array(3)].map((_, i) => (
                  <div key={i} className="bg-[#CFC7DF] rounded-2xl p-5 space-y-3">
                    <Skeleton className="w-full h-40" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))
                : similarProducts.map((item) => (
                  <a
                    key={item.prod_id || item.id}
                    href={`/products/${item.prod_id || item.id}`}
                    className="bg-[#CFC7DF] rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition transform hover:-translate-y-1"
                  >
                    <div className="bg-gray-100 flex items-center justify-center h-44">
                      {item.image_url || item.image ? (
                        <img
                          src={"https://cosmelytix-products-imgs.s3.us-east-1.amazonaws.com/" + (item.image_url || item.image)}
                          alt={item.name}
                          className="h-full w-full object-contain p-4"
                        />
                      ) : (
                        <Skeleton className="w-full h-full" />
                      )}
                    </div>
                    <div className="p-4 space-y-1">
                      <p className="font-semibold text-gray-900 text-sm line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.brand || ''}</p>
                    </div>
                  </a>
                ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ProductProfilePage;
